import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject, Input,
  OnInit,
  ViewChild } from '@angular/core';

import { IQuestionEventsService, QUESTION_EVENTS_SERVICE_TOKEN } from '../../../../services/IQuestionEvents';
import { applyFrameToElement, parseFrameString } from '../../../../utils/utils';
import { InputColor } from '../../../enums/input-color.enum';
import { IScorable, ScorableData } from '../kh-manip-scorable/IScorable';

@Component({
  selector: 'kh-manipulative-string-input',
  templateUrl: 'kh-manip-scorable-input.component.html',
  styleUrls: ['kh-manip-scorable-input.component.less'],
  standalone: false
})
export class KhManipScorableInput implements IScorable, AfterViewInit, OnInit {
  @Input() public value = '';
  @Input() public key = '';
  @Input() public name = '';
  @Input() public id = '';
  @Input() public frame = '0 0 100 100';
  @Input('font-family') protected fontFamily = 'arial';
  @Input() protected strings = '';
  @Input('max-length') public maxLength = '40';
  @Input('text-size') public textSize = '80%';
  @Input() public type = 'text';
  @Input('border-radius') public borderRadius = 5;
  @Input('border-stroke-size') public borderStrokeSize = 3;
  @Input('background-color') public backgroundColor = 'transparent';
  @Input('text-alignment') public textAlignment = 'center';
  @Input('text-style') public fontStyle = 'normal';
  @Input('broken-keys') public brokenKeys: number[] = [];
  @ViewChild('scorableInput') public inputElement!: ElementRef<HTMLInputElement>;
  public onChangeCallback: undefined | ((data: ScorableData) => void); // callback

  public fontWeight = '';
  public borderColor = InputColor.DEFAULT_COLOR;
  public fontSize = 16;

  public constructor(@Inject(QUESTION_EVENTS_SERVICE_TOKEN) protected readonly questionEventsService: IQuestionEventsService) {
    this.questionEventsService.hideInputOnlyForManip.next(true);
  }

  public ngOnInit() {
    this.removeStroke();
    const frame = parseFrameString(this.frame);
    this.fontSize = this.textSize.endsWith('%') ? Number(this.textSize.replace('%', '')) / 100 * frame.height : Number(this.textSize);
  }

  public ngAfterViewInit(): void {
    if (this.inputElement?.nativeElement) {
      this.inputElement.nativeElement.value = this.value;
      this.inputElement.nativeElement.name = this.name;
      this.inputElement.nativeElement.id = this.id;
      this.inputElement.nativeElement.style.fontFamily = this.fontFamily;
      this.inputElement.nativeElement.style.fontSize = `${this.fontSize}px`;
      this.inputElement.nativeElement.style.backgroundColor = this.backgroundColor;
      this.inputElement.nativeElement.style.borderColor = this.borderColor;
      this.inputElement.nativeElement.style.borderRadius = `${this.borderRadius}px`;
      this.inputElement.nativeElement.style.borderWidth = `${this.borderStrokeSize}px`;
      this.inputElement.nativeElement.style.textAlign = this.textAlignment;
      this.inputElement.nativeElement.style.fontStyle = this.fontStyle;
      this.inputElement.nativeElement.style.fontWeight = this.fontStyle;
      this.inputElement.nativeElement.type = this.type;
      this.inputElement.nativeElement.maxLength = Number(this.maxLength);
    }

    if (this.inputElement?.nativeElement) {
      applyFrameToElement(this.inputElement.nativeElement, this.frame);
      this.inputElement.nativeElement.addEventListener('input', () => { this.onChange(); });
      this.inputElement.nativeElement.value = this.value;
      this.inputElement.nativeElement.addEventListener('focus', () => { this.setStroke(); });
      this.inputElement.nativeElement.addEventListener('blur', () => { this.removeStroke(); });
      this.inputElement.nativeElement.addEventListener('onwheel', () => { this.removeStroke(); });
    }
  }

  private onChange(): void {
    const newValue = this.inputElement?.nativeElement.value;

    // Number inputs weren't truncating length correctly
    const length = Number(this.maxLength);

    // Number inputs need special handling for max length
    if (this.type.toLocaleLowerCase() === 'number' && newValue.length > length) {
      this.inputElement.nativeElement.value = this.value;
      return;
    }

    this.value = this.inputElement?.nativeElement.value;

    if (this.onChangeCallback) {
      this.onChangeCallback(this.getScorableData());
    }

    this.removeStroke();
  }

  public getScorableData(): ScorableData {
    let sanitizedAnswer = this.value;

    if (sanitizedAnswer.includes(',')) {
      sanitizedAnswer = `"${sanitizedAnswer}"`;
    }

    const data: ScorableData = {
      answer: { [this.key]: sanitizedAnswer },
      state: { [this.key]: sanitizedAnswer }
    };

    return data;
  }

  public setScorableData(data: ScorableData): void {
    const keys = Object.keys(data.answer);

    for (let i = 0; i < keys.length; i++) {
      if (keys[i] === this.key) {
        const val = data.answer[keys[i]];

        if (val.includes(',')) {
          // if this was a comma separated value, we need to remove the quotes
          this.value = val.replace(/"/g, '');
        } else {
          this.value = val;
        }

        this.inputElement.nativeElement.value = this.value;

        return;
      }
    }
  }

  public setStroke(): void {
    this.borderColor = InputColor.ACTIVE_COLOR;
  }

  public removeStroke(): void {
    this.borderColor = this.value ? InputColor.ACTIVE_COLOR : InputColor.DEFAULT_COLOR;
  }
}
